import {
  Button,
  ButtonGroup,
  FieldSet,
  Heading,
  Label,
  LabeledControl,
  Legend,
  ModalDialog,
  Radio,
  TextButton,
} from '@zillow/constellation';
import React, { useState } from 'react';
import type { InvitedByContactInfo, ReceivedCoapplicantInvitation } from '../../../../graphql/queries';

interface Props {
  isOpen: boolean;
  onCloseModal: () => void;
  invitations: Array<ReceivedCoapplicantInvitation>;
  onJoinApplicationClick: (rentalApplicationInviteId: string) => void;
}

export const joinButtonText = 'Join application';

export function formatDisplayName(firstName: string, lastName: string, email: string) {
  return `${firstName || ''} ${lastName || ''}`.trim() || email;
}

export function MultipleInvitesModal({ isOpen, invitations, onCloseModal, onJoinApplicationClick }: Props) {
  const [selectedRentalApplicationInviteId, setSelectedRentalApplicationInviteId] = useState('');
  const [areButtonsDisabled, setAreButtonsDisabled] = useState(false);

  const inviteRadioButtons = invitations.map(({ invitedBy, rentalApplicationInviteId }) => {
    const inviteId = rentalApplicationInviteId as string;
    const inviteInfo = invitedBy as InvitedByContactInfo;
    const firstName = inviteInfo?.firstName ?? '';
    const lastName = inviteInfo?.lastName ?? '';
    const email = (inviteInfo?.email as string) ?? '';
    const displayName = formatDisplayName(firstName, lastName, email);

    return (
      <LabeledControl
        key={inviteId}
        label={<Label>{displayName}</Label>}
        control={
          <Radio
            onChange={() => {
              setSelectedRentalApplicationInviteId(inviteId);
            }}
          />
        }
      />
    );
  });

  function onSubmitClick() {
    setAreButtonsDisabled(true);
    onJoinApplicationClick(selectedRentalApplicationInviteId);
    onCloseModal();
  }

  function onClose() {
    onCloseModal();
  }

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      size={{ default: 'fullScreen', md: 'sm' }}
      renderHeader={<Heading level={1}>Join an existing application?</Heading>}
      renderBody={
        <FieldSet legend={<Legend>Select the person you’d like to apply with:</Legend>}>{inviteRadioButtons}</FieldSet>
      }
      renderFooter={
        <ButtonGroup aria-label="Join application options">
          <TextButton onClick={onClose} disabled={areButtonsDisabled} type="button">
            Cancel
          </TextButton>
          <Button
            onClick={() => onSubmitClick()}
            disabled={areButtonsDisabled || !selectedRentalApplicationInviteId}
            buttonType="primary"
            type="button"
          >
            {joinButtonText}
          </Button>
        </ButtonGroup>
      }
    />
  );
}
